import { apiClient, objectToQueryString } from "@wtf/shared/api/utils/api-client";
import { CollectionDto } from "@wtf/shared";
import { ProductDto } from "@wtf/shared";

const BASE_ENDPOINT = "api/featured/";

/**
 * Parameters for fetching featured collections (infinite scroll with trending + pseudo-random mix)
 */
export interface FeaturedCollectionsParams {
  /** Number of items to skip (preferred for infinite scroll) */
  offset?: number;
  /** Page number (1-indexed, legacy support) */
  page?: number;
  /** How many items per page (default: 14) */
  limit?: number;
  /** How many trending items to include per page (default is limit/2) */
  trendingCount?: number;
  /** Comma-separated list of collection IDs to exclude from results */
  excludeIds?: string;
  /** Seed for consistent pseudo-random ordering across requests */
  seed?: string;
}

/**
 * Response shape for featured collections endpoint
 */
export interface FeaturedCollectionsResponse {
  /** Current offset position */
  offset: number;
  /** Items per page */
  limit: number;
  /** Whether more items are available */
  hasMore: boolean;
  /** Offset for next page */
  nextOffset: number;
  /** Array of featured collections */
  items: CollectionDto[];
  /** Seed used for pseudo-random ordering */
  seed: string;
  /** Current page (only included if page parameter was used) */
  page?: number;
}

/**
 * Parameters for fetching featured products (infinite scroll with trending + pseudo-random mix)
 */
export interface FeaturedProductsParams {
  /** Number of items to skip (preferred for infinite scroll) */
  offset?: number;
  /** Page number (1-indexed, legacy support) */
  page?: number;
  /** How many items per page (default: 14) */
  limit?: number;
  /** How many trending items to include per page (default is limit/2) */
  trendingCount?: number;
  /** Comma-separated list of product IDs to exclude from results */
  excludeIds?: string;
  /** Comma-separated list of root product IDs to exclude from results (prevents visual duplicates across copies) */
  excludeRootIds?: string;
  /** Comma-separated list of image URLs to exclude from results (prevents identical images) */
  excludeImageUrls?: string;
  /** Seed for consistent pseudo-random ordering across requests */
  seed?: string;
  /** Minimum price filter (inclusive) */
  minPrice?: number;
  /** Maximum price filter (inclusive) */
  maxPrice?: number;
  /** Currency for price filters (defaults to USD if not provided) */
  currency?: string;
  /** Filter by season (SPRING_SUMMER or AUTUMN_WINTER) */
  season?: string;
  /** Filter by gender category (MENSWEAR, WOMENSWEAR, or UNISEX) */
  gender?: string;
  /** Filter by level 1 category */
  categoryL1?: string;
  /** Filter by level 2 category */
  categoryL2?: string;
  /** Filter by level 3 category */
  categoryL3?: string;
}

/**
 * Response shape for featured products endpoint
 */
export interface FeaturedProductsResponse {
  /** Current offset position */
  offset: number;
  /** Items per page */
  limit: number;
  /** Whether more items are available */
  hasMore: boolean;
  /** Offset for next page */
  nextOffset: number;
  /** Array of featured products */
  items: ProductDto[];
  /** Seed used for pseudo-random ordering */
  seed: string;
  /** Current page (only included if page parameter was used) */
  page?: number;
}

/**
 * Get featured collections (infinite scroll with trending + pseudo-random mix)
 */
export async function getFeaturedCollections(
  params: FeaturedCollectionsParams = {}
): Promise<FeaturedCollectionsResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<FeaturedCollectionsResponse>(`${BASE_ENDPOINT}collections${queryString}`);
}

/**
 * Get featured products (infinite scroll with trending + pseudo-random mix)
 */
export async function getFeaturedProducts(params: FeaturedProductsParams = {}): Promise<FeaturedProductsResponse> {
  const queryString = objectToQueryString(params);
  return apiClient<FeaturedProductsResponse>(`${BASE_ENDPOINT}products${queryString}`);
}

export const featuredApi = {
  getFeaturedCollections,
  getFeaturedProducts,
};
